<template>
  <account-modal class="edit-account-modal">
    <template slot="heading">Edit account</template>

    <template slot="content">
      <v-text-field
        v-model="form.name"
        label="Account name"
        :error-messages="getValidationErrorMessage('name')"
      />

      <v-layout
        class="row--data mt-2"
        row
      >
        <v-flex xs6>
          <v-subheader class="v-subheader--dense">Manage requisites</v-subheader>
        </v-flex>

        <v-flex xs6>
          <template v-if="requisitesOptions.length">
            <v-checkbox
              v-for="(option, key) in requisitesOptions"
              :key="key"
              v-model="form.allowedRequisites"
              color="primary"
              :label="option.name"
              :value="option.value"
              hide-details
            />
            <div
              v-if="getValidationErrorMessage('allowedRequisites')"
              class="v-messages error--text mt-2"
            >
              {{ getValidationErrorMessage('allowedRequisites') }}
            </div>
          </template>
          <template v-else>No available</template>
        </v-flex>
      </v-layout>

      <v-layout
        class="row--data mt-2"
        row
      >
        <v-flex xs6>
          <v-subheader class="v-subheader--dense">Allowed incoming channels</v-subheader>
        </v-flex>

        <v-flex xs6>
          <template v-if="incomingChannelOptions.length">
            <v-checkbox
              v-for="(option, key) in incomingChannelOptions"
              :key="key"
              v-model="form.allowedIncomingChannels"
              color="primary"
              :label="option.name"
              :value="option.value"
              :error-messages="getValidationErrorMessage('allowedIncomingChannels')"
              hide-details
            />
          </template>
          <template v-else>No available</template>
        </v-flex>
      </v-layout>

      <v-layout
        class="row--data mt-2"
        row
      >
        <v-flex xs6>
          <v-subheader class="v-subheader--dense">Allowed outgoing channels</v-subheader>
        </v-flex>

        <v-flex xs6>
          <template v-if="outgoingChannelOptions.length">
            <v-checkbox
              v-for="(option, key) in outgoingChannelOptions"
              :key="key"
              v-model="form.allowedOutgoingChannels"
              color="primary"
              :label="option.name"
              :value="option.value"
              :error-messages="getValidationErrorMessage('allowedOutgoingChannels')"
              hide-details
            />
          </template>
          <template v-else>No available</template>
        </v-flex>
      </v-layout>

      <error-alert :error="error" />
    </template>

    <template slot="actions">
      <v-btn
        color="light-blue darken-3"
        flat
        :disabled="loading"
        @click="onSaveButtonClick"
      >save</v-btn>

      <v-btn
        color="light-blue darken-3"
        flat
        @click="onCancelButtonClick"
      >cancel</v-btn>
    </template>
  </account-modal>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'

  // Events
  import { EVENT_ACCOUNT_UPDATE } from '@/events/accounts'

  // Models
  import Payment, { PAYMENT_CHANNELS } from '@/models/Payment'

  // Services
  import ApiRequest from '@/services/ApiRequests/ApiRequest'
  import AccountApi from '@/services/Api/AccountApi'

  // Mixins
  import form from '@/mixins/form'

  // Component
  import AccountModal from '@/components/accounts/AccountModal'
  import ErrorAlert from '@/components/ui/ErrorAlert'

  export const ALLOWED_REQUISITES = [
    PAYMENT_CHANNELS.BLUOR_BANK,
    PAYMENT_CHANNELS.BANK_OF_LITHUANIA,
    PAYMENT_CHANNELS.CZCB,
    PAYMENT_CHANNELS.IFAST,
    PAYMENT_CHANNELS.MANO,
    PAYMENT_CHANNELS.INCORE,
    PAYMENT_CHANNELS.BO_US,
    PAYMENT_CHANNELS.FV_BANK
  ]

  export const ALLOWED_INCOMING_CHANNELS = [
    PAYMENT_CHANNELS.BLUOR_BANK,
    PAYMENT_CHANNELS.BANK_OF_LITHUANIA,
    PAYMENT_CHANNELS.BILDER_ACCOUNT,
    PAYMENT_CHANNELS.CZCB,
    PAYMENT_CHANNELS.IFAST,
    PAYMENT_CHANNELS.MANO,
    PAYMENT_CHANNELS.PRIVALGO,
    PAYMENT_CHANNELS.INCORE,
    PAYMENT_CHANNELS.BO_US,
    PAYMENT_CHANNELS.FV_BANK
  ]

  export const ALLOWED_OUTGOING_CHANNELS = [
    PAYMENT_CHANNELS.BLUOR_BANK,
    PAYMENT_CHANNELS.BANK_OF_LITHUANIA,
    PAYMENT_CHANNELS.BILDER_ACCOUNT,
    PAYMENT_CHANNELS.CZCB,
    PAYMENT_CHANNELS.IFAST,
    PAYMENT_CHANNELS.MANO,
    PAYMENT_CHANNELS.PRIVALGO,
    PAYMENT_CHANNELS.INCORE,
    PAYMENT_CHANNELS.BO_US,
    PAYMENT_CHANNELS.FV_BANK
  ]

  export default {
    name: 'EditAccountModal',
    components: {
      AccountModal,
      ErrorAlert
    },
    mixins: [form],
    data () {
      return {
        accountEditApiRequest: new ApiRequest(AccountApi.update),
        form: {
          name: null,
          allowedRequisites: [],
          allowedIncomingChannels: [],
          allowedOutgoingChannels: []
        }
      }
    },
    computed: {
      ...mapGetters('modal', ['modal']),
      requisitesOptions () {
        if (this.profileLicense === 'UNKNOWN') return []
        const allowedRequisites = ALLOWED_REQUISITES

        if (allowedRequisites == null) {
          return []
        }

        const options = Payment.channelOptions.filter(({ value }) => allowedRequisites.includes(value))
        const withoutLicense = options.filter(({ license }) => license === undefined)
        const matched = options.filter(
          ({ license }) => license === this.profileLicense
        )

        const others = options.filter(
          ({ license }) => license !== undefined && license !== this.profileLicense
        )
        return [...withoutLicense, ...matched, ...others]
      },
      incomingChannelOptions () {
        if (this.profileLicense === 'UNKNOWN') return []
        const allowedChannels = ALLOWED_INCOMING_CHANNELS

        if (allowedChannels == null) {
          return []
        }

        const options = Payment.channelOptions.filter(({ value }) => allowedChannels.includes(value))
        const withoutLicense = options.filter(({ license }) => license === undefined)
        const matched = options.filter(
          ({ license }) => license === this.profileLicense
        )

        const others = options.filter(
          ({ license }) => license !== undefined && license !== this.profileLicense
        )
        return [...withoutLicense, ...matched, ...others]
      },
      outgoingChannelOptions () {
        if (this.profileLicense === 'UNKNOWN') return []
        const allowedChannels = ALLOWED_OUTGOING_CHANNELS

        if (allowedChannels == null) {
          return []
        }

        const options = Payment.channelOptions.filter(({ value }) => allowedChannels.includes(value))
        const withoutLicense = options.filter(({ license }) => license === undefined)
        const matched = options.filter(
          ({ license }) => license === this.profileLicense
        )

        const others = options.filter(
          ({ license }) => license !== undefined && license !== this.profileLicense
        )
        return [...withoutLicense, ...matched, ...others]
      },
      account () {
        return this.modal.data.account
      },
      profileId () {
        return this.modal.data.profileId
      },
      profileLicense () {
        return this.modal.data.profile.license
      },
      requestData () {
        return this.parsedFormInput
      },
      loading () {
        return this.accountEditApiRequest.loading
      },
      error () {
        return this.accountEditApiRequest.error
      }
    },
    created () {
      this.$set(this, 'form', this.formatInitialFormData(this.account))
    },
    methods: {
      ...mapActions('modal', ['showModal', 'closeModal']),
      formatInitialFormData (account) {
        return {
          name: account.name,
          allowedRequisites: account.allowedRequisites,
          allowedIncomingChannels: account.allowedIncomingChannels,
          allowedOutgoingChannels: account.allowedOutgoingChannels
        }
      },
      onSaveButtonClick () {
        this.accountEditApiRequest
          .execute(this.account.accountId, this.requestData)
          .then(this.accountEditSuccessHandler)
          .catch(() => {})
      },
      onCancelButtonClick () {
        this.closeModal()
      },
      accountEditSuccessHandler () {
        this.$eventHub.$emit(EVENT_ACCOUNT_UPDATE, this.profileId)
        this.closeModal()

        setTimeout(() => {
          this.showModal({
            name: 'SuccessFlashModal',
            data: { message: 'Account has been successfully edited.' }
          })
        }, 100)
      }
    }
  }
</script>
